import { flattenTagsToDepth } from "@helpers/content/tag";
import { useAppStore } from "@hooks/useAppStore";
import type { SessionConfigFiltersType } from "@memorang/constants/config/schema";
import type { MixedItemTag, TagsByType } from "@memorang/types";
import type { SectionTag } from "@memorang/ui/components/FilterSections/types";
import type { Distribution } from "@memorang/ui/types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	applySubtreeExclusiveToggle,
	computeCheckedTagsDist,
} from "../helpers/filter";
import {
	TAG_TYPES,
	applySkillSelection,
	buildQuickStudyTagSelection,
	computeStructuredTagsDist,
	extractQuestionTypeSelection,
	pruneUnavailableSelections,
	stripLockedTagIds,
} from "../helpers/session-filters";
import { LearningModes } from "../types";
import type { CheckboxState } from "../types/filter";
import { useDistributionState } from "./useDistributionState";
import { useStructuredTagFilters } from "./useStructuredTagFilters";

export type AdditionalParams = {
	tagsByType?: TagsByType;
	studyRecommendedTopics?: boolean;
	preferRecommendedTab?: boolean;
	hasAccess?: boolean;
	freeTagIds?: number[];
	selectedTagIds?: number[];
	sectionTags?: SectionTag[];
	recommendationTags?: MixedItemTag[];
	numItemsMap?: Record<string, number>;
	premiumSkills?: string[];
	premiumTagsByType?: Record<string, MixedItemTag[]>;
	/** The general study pack's raw tags (ids stable across collections), used to scope the premium banner to the selected skill. */
	premiumStudyPackTags?: MixedItemTag[];
};

const useSessionConfig = ({
	availableDistribution,
	additionalParams,
	isSessionCreationInProgress,
	showLearningMode,
	showDistributionFilters,
	distributionFilterItems,
}: {
	availableDistribution: Distribution;
	additionalParams: AdditionalParams;
	isSessionCreationInProgress?: boolean;
	showLearningMode?: boolean;
	showDistributionFilters?: boolean;
	distributionFilterItems: SessionConfigFiltersType;
}) => {
	const {
		tagsByType,
		hasAccess,
		freeTagIds,
		selectedTagIds,
		studyRecommendedTopics,
	} = additionalParams;

	const showPointsBasedScore = useAppStore(
		(state) => state.reportView?.formative?.showPointsBasedScore,
	);
	const crossTagSelection = useAppStore(
		(state) => state.app.sessionConfig?.crossTagSelection,
	);
	const maxFilterDepth = useAppStore(
		(state) => state.app.sessionConfig?.maxFilterDepth ?? 1,
	);

	const [selectedLearningMode, setSelectedLearningMode] = useState<string>(
		LearningModes.questions,
	);
	const [selectedTagCheckboxMap, setSelectedTagCheckboxMap] =
		useState<CheckboxState>();
	const skillQuestionTypeSelectionsRef = useRef<Record<number, CheckboxState>>(
		{},
	);

	const {
		finalAvailableDist,
		requiredDistribution,
		numRequiredItems,
		availableItems,
		selectedDistributionCheckboxMap,
		numTotalItemsFiltered,
		handleDistributionCheckboxState,
		handleSliderChange,
		selectedDurationMinutes,
		handleDurationChange,
		applyAvailableDistribution,
	} = useDistributionState({
		availableDistribution,
		distributionFilterItems,
		showDistributionFilters,
		studyRecommendedTopics,
	});

	const topLevelTags = useMemo(
		() => (tagsByType ? Object.values(tagsByType).flat() : []),
		[tagsByType],
	);

	const allTags = useMemo(
		() => flattenTagsToDepth(topLevelTags, maxFilterDepth),
		[topLevelTags, maxFilterDepth],
	);

	const { hasSkillTags, unavailableQuestionTypeIds, structuredTagCounts } =
		useStructuredTagFilters({
			topLevelTags,
			selectedTagCheckboxMap,
			selectedDistributionCheckboxMap,
		});

	const handleSetLearningMode = useCallback((mode: string) => {
		setSelectedLearningMode(mode);
	}, []);

	const handleTagCheckboxStates = useCallback(
		(incomingStatus: CheckboxState) => {
			const status = stripLockedTagIds(incomingStatus, topLevelTags);
			const newDist = hasSkillTags
				? computeStructuredTagsDist(
						topLevelTags,
						status,
						distributionFilterItems,
					)
				: computeCheckedTagsDist({
						status,
						allTags,
						topLevelTags,
						freeTagIds,
						hasAccess,
						crossTagSelection,
						learningMode: selectedLearningMode,
						showLearningMode,
						distributionFilterItems,
						showPointsBasedScore,
					});

			setSelectedTagCheckboxMap(status);
			applyAvailableDistribution(newDist);
		},
		[
			hasSkillTags,
			topLevelTags,
			allTags,
			freeTagIds,
			hasAccess,
			crossTagSelection,
			selectedLearningMode,
			showLearningMode,
			distributionFilterItems,
			showPointsBasedScore,
			applyAvailableDistribution,
		],
	);

	useEffect(() => {
		if (isSessionCreationInProgress) {
			return;
		}

		let status: CheckboxState;

		if (hasSkillTags) {
			status = buildQuickStudyTagSelection(topLevelTags, selectedTagIds);
		} else if (selectedTagIds?.length) {
			status = {};
			for (const { id } of allTags) {
				status[id] = selectedTagIds.includes(id);
			}
		} else {
			status = {};
			for (const { id } of allTags) {
				status[id] = false;
			}
		}

		handleTagCheckboxStates(status);
	}, [
		allTags,
		topLevelTags,
		handleTagCheckboxStates,
		selectedTagIds,
		isSessionCreationInProgress,
		hasSkillTags,
	]);

	const handleSetTagCheckboxMap = useCallback(
		(selectedCheckboxId: string) => {
			const tagId = Number(selectedCheckboxId);
			const tappedTag = topLevelTags.find((t) => t.id === tagId);
			const applyStatus = (status: CheckboxState) =>
				handleTagCheckboxStates(
					hasSkillTags
						? pruneUnavailableSelections(
								topLevelTags,
								status,
								selectedDistributionCheckboxMap,
							)
						: status,
				);

			if (hasSkillTags && tappedTag?.type === TAG_TYPES.skill) {
				const previousSkillTag = topLevelTags.find(
					(tag) =>
						tag.type === TAG_TYPES.skill && selectedTagCheckboxMap?.[tag.id],
				);
				if (previousSkillTag) {
					skillQuestionTypeSelectionsRef.current[previousSkillTag.id] =
						extractQuestionTypeSelection(topLevelTags, selectedTagCheckboxMap);
				}

				applyStatus(
					applySkillSelection(
						topLevelTags,
						selectedTagCheckboxMap,
						tappedTag,
						skillQuestionTypeSelectionsRef.current[tappedTag.id],
					),
				);
				return;
			}

			applyStatus(
				applySubtreeExclusiveToggle(
					selectedTagCheckboxMap,
					tagId,
					topLevelTags,
				),
			);
		},
		[
			selectedTagCheckboxMap,
			selectedDistributionCheckboxMap,
			topLevelTags,
			hasSkillTags,
			handleTagCheckboxStates,
		],
	);

	return {
		selectedLearningMode,
		handleSetLearningMode,
		selectedTagCheckboxMap,
		handleSetTagCheckboxMap,
		finalAvailableDist,
		requiredDistribution,
		numRequiredItems,
		selectedDistributionCheckboxMap,
		handleDistributionCheckboxState,
		availableItems,
		numTotalItemsFiltered,
		handleSliderChange,
		selectedDurationMinutes,
		handleDurationChange,
		unavailableQuestionTypeIds,
		structuredTagCounts,
	};
};

export default useSessionConfig;
